import { fme, keyFromLabel } from '@go-shared/lib/utils';
import { ReservableQuestionField, ReservableQuestionSignature } from '@go-shared/types/reservable';
import { FormikProps } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import { BubbleCheckbox, BubbleFormGroup, BubbleInput, BubbleTextArea } from './BubbleUI';
import { BubbleInputProps } from './BubbleUI/BubbleInput';
import { ReservationFormikForm } from './Formik/ReservationFormik';
import { onlyUnique } from '@go-shared/lib/functional';
import phone from 'phone';
import { stringToTimeIfValid, timeToString } from '@go-shared/lib/time';

const SCROLL_BOTTOM_THRESHOLD_PX = 8;

interface SignatureQuestionFillerProps {
  field: ReservableQuestionSignature;
  value: string;
  onValue: (checked: boolean) => void;
}

function isAtBottom(el: HTMLDivElement) {
  return el.scrollTop + el.clientHeight >= el.scrollHeight - SCROLL_BOTTOM_THRESHOLD_PX;
}

function SignatureQuestionFiller(props: SignatureQuestionFillerProps) {
  const { field, value, onValue } = props;
  const checked = value === 'agreed';
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(checked);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    function handleScroll() {
      if (el && isAtBottom(el)) setHasScrolledToBottom(true);
    }

    // Also covers content that already fits without overflowing, which never fires a scroll event.
    handleScroll();
    el.addEventListener('scroll', handleScroll);
    return () => el.removeEventListener('scroll', handleScroll);
  }, [field.settings.content]);

  return (
    <div className="grid grid-cols-1 gap-2">
      <div
        ref={scrollRef}
        className="html-view w-full rounded border px-3 py-2 text-sm overflow-y-auto"
        style={{ maxHeight: 220 }}
        dangerouslySetInnerHTML={{ __html: field.settings.content }}
      />
      <BubbleCheckbox
        checked={checked}
        disabled={!hasScrolledToBottom}
        onValue={onValue}
        label={
          hasScrolledToBottom
            ? field.settings.checkboxLabel || 'I agree'
            : 'Scroll down to continue reading ↓'
        }
      />
    </div>
  );
}

export interface ReservationQuestionFillerProps {
  formik: FormikProps<ReservationFormikForm>;
  formikPath: string;
  fields: ReservableQuestionField[];
  values: {
    [key: string]: string;
  };
}

export default function ReservationQuestionFiller(props: ReservationQuestionFillerProps) {
  const { formik, fields, formikPath, values } = props;

  // Helper function to create properly typed input props
  function createInputProps(
    name: string,
    field: ReservableQuestionField,
    value: string,
    option: string = ''
  ): BubbleInputProps {
    // Only input fields have settings.type
    if (field.type !== 'input') {
      return {
        formik: [formik, name],
        type: 'text' as const,
        value: value || '',
      };
    }

    switch (field.settings.type) {
      case 'number':
        return {
          formik: [formik, name],
          type: 'text' as const,
          inputMode: 'numeric',
          pattern: '[0-9]*',
          value: value || '',
        };

      case 'email':
        return {
          formik: [formik, name],
          type: 'email' as const,
          value: value || '',
        };

      case 'date':
        return {
          formik: [formik, name],
          type: 'date' as const,
          value: value || undefined,
        };

      case 'checkbox':
      case 'radio':
        const valueArray = value.split(',').filter(v => !!v);

        return {
          formik: [formik, name],
          type: 'checkbox' as const,
          value: option || '',
          appearance: field.settings.type === 'radio' ? 'rounded' : undefined,
          inputClassName: 'stretched-link',
          checked: field.settings.type === 'radio' ? value === option : valueArray.includes(option),
          onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
            const checked = e.target.checked;
            let newValue = '';

            if (field.settings.type === 'radio') {
              newValue = checked ? option : '';
            } else {
              if (checked) {
                newValue = valueArray.concat([option]).filter(onlyUnique).join(',');
              } else {
                newValue = valueArray.filter(v => v !== option).join(',');
              }
            }

            formik.setFieldValue(`${formikPath}.${keyFromLabel(field.label)}`, newValue);
          },
        };

      case 'text':
      default:
        return {
          formik: [formik, name],
          type: 'text' as const,
          value: value || '',
        };
    }
  }

  return (
    <div className="reservation-ticket-filler grid grid-cols-1 gap-4">
      {fields.map(field => {
        const key = keyFromLabel(field.label);

        if (field.type === 'input') {
          if (['phone'].includes(field.settings.type)) {
            const valToUse = values[key] || '';
            const parsedPhone = phone(valToUse);

            return (
              <BubbleFormGroup
                key={field._id}
                label={field.label}
                error={fme(formik.errors, formik.touched, `${formikPath}.${key}` as any)}
                labelFn={props => (
                  <div className="flex items-center">
                    <span>{props.label}</span>
                    {field.required && <span className="text-crimson">&nbsp;*</span>}
                    <div className="ml-auto text-xs text-smoke italic">
                      {!valToUse || parsedPhone.isValid ? parsedPhone.phoneNumber : 'Invalid Phone'}
                    </div>
                  </div>
                )}
              >
                <BubbleInput block {...createInputProps(`${formikPath}.${key}`, field, valToUse)} />
              </BubbleFormGroup>
            );
          } else if (['time'].includes(field.settings.type)) {
            const valToUse = values[key] || '';
            const timeValue = stringToTimeIfValid(valToUse);

            return (
              <BubbleFormGroup
                key={field._id}
                label={field.label}
                asterisk={field.required}
                error={fme(formik.errors, formik.touched, `${formikPath}.${key}` as any)}
              >
                <BubbleInput
                  block
                  type="time"
                  value={timeValue}
                  onValue={t =>
                    formik.setFieldValue(
                      `${formikPath}.${key}`,
                      t ? timeToString(t, '24', 'default') : ''
                    )
                  }
                />
              </BubbleFormGroup>
            );
          } else if (['date', 'number', 'email', 'text'].includes(field.settings.type)) {
            return (
              <BubbleFormGroup
                key={field._id}
                label={field.label}
                asterisk={field.required}
                error={fme(formik.errors, formik.touched, `${formikPath}.${key}` as any)}
              >
                <BubbleInput
                  block
                  {...createInputProps(`${formikPath}.${key}`, field, values[key] || '')}
                />
              </BubbleFormGroup>
            );
          } else if (['checkbox', 'radio'].includes(field.settings.type)) {
            return (
              <BubbleFormGroup
                key={field._id}
                label={field.label}
                asterisk={field.required}
                error={fme(formik.errors, formik.touched, `${formikPath}.${key}` as any)}
              >
                <div className="grid grid-cols-1 gap-1">
                  {field.settings.options.map(opt => (
                    <BubbleCheckbox
                      key={opt._id}
                      {...(createInputProps(
                        `${formikPath}.${key}`,
                        field,
                        values[key] || '',
                        opt.name
                      ) as any)} // FIXME: dirty hack!~
                      label={opt.name}
                    />
                  ))}
                </div>
              </BubbleFormGroup>
            );
          } else {
            return null;
          }
        } else if (field.type === 'textarea') {
          return (
            <BubbleFormGroup
              key={field._id}
              label={field.label}
              asterisk={field.required}
              error={fme(formik.errors, formik.touched, `${formikPath}.${key}` as any)}
            >
              <BubbleTextArea
                name={`${formikPath}.${key}`}
                block
                value={values[key] || ''}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            </BubbleFormGroup>
          );
        } else if (field.type === 'signature') {
          return (
            <BubbleFormGroup
              key={field._id}
              label={field.label}
              asterisk={field.required}
              error={fme(formik.errors, formik.touched, `${formikPath}.${key}` as any)}
            >
              <SignatureQuestionFiller
                field={field}
                value={values[key] || ''}
                onValue={checked => formik.setFieldValue(`${formikPath}.${key}`, checked ? 'agreed' : '')}
              />
            </BubbleFormGroup>
          );
        } else {
          return null;
        }
      })}
    </div>
  );
}
